import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/constants";
import ScrollReveal from "@/components/ScrollReveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Sprout, HeartPulse, GraduationCap, Users, Megaphone, ShieldAlert, ArrowRight, Quote, Handshake, Building, Network, ShieldCheck, Star } from "lucide-react";

const stats = [
  { value: 5000, suffix: "+", label: "Farmers Empowered" },
  { value: 1800, suffix: "+", label: "Families Served" },
  { value: 100, suffix: "+", label: "Villages Reached" },
  { value: 20, suffix: "+", label: "Years of Service" },
];

const programmes = [
  { icon: Sprout, title: "Agriculture & FPO", desc: "Supporting 5,000+ farmers with certified seeds, modern tools, sustainable farming training, collective bargaining, and direct market linkages — eliminating middlemen.", color: "#2D6A4F" },
  { icon: HeartPulse, title: "Healthcare", desc: "Mobile health clinics, blood donation camps, nutrition drives, preventive health camps, and community hospitals offering Rs.5 treatment for weaker families.", color: "#40916C" },
  { icon: GraduationCap, title: "Education", desc: "Rural school support, scholarships for tribal students, digital literacy drives, and partnerships with urban institutions for mentoring.", color: "#1B4332" },
  { icon: Users, title: "Women Empowerment", desc: "Self-help groups, vocational training, financial inclusion, micro-enterprise development, and leadership for women in rural governance.", color: "#2D6A4F" },
  { icon: Megaphone, title: "Youth Leadership", desc: "Campus chapters, civic engagement, leadership workshops, social entrepreneurship incubation, and structured volunteer programmes.", color: "#40916C" },
  { icon: ShieldAlert, title: "Disaster Relief", desc: "Rapid-response emergency aid — ration distribution, temporary shelter, rehabilitation, and long-term recovery for crisis-affected communities.", color: "#1B4332" },
];

const modelSteps = [
  { icon: Handshake, title: "Embed, Don't Parachute", desc: "We live within the communities we serve. Our team members are from Jharkhand." },
  { icon: Building, title: "Build Institutions", desc: "Schools, hospitals, FPOs — permanent infrastructure that serves for generations." },
  { icon: Network, title: "Integrate Every Vertical", desc: "One family, multiple touchpoints — healthcare, education, agriculture, compounding impact." },
  { icon: ShieldCheck, title: "Local Governance", desc: "Connecting citizens to district officials through Rakshak24 for real problem-solving." },
];

const blogPosts = [
  {
    image: IMAGES.healthcare,
    date: "November 22, 2025",
    title: "Blood Donation Camp at SGVS Hospital, Khunti",
    excerpt: "MBT Trust organized a major blood donation camp in partnership with Jharkhand Health Department at SGVS Hospital premises.",
  },
  {
    image: IMAGES.childrenBanner2,
    date: "October 15, 2025",
    title: "School Nutrition Drive Reaches 500+ Children in Khunti",
    excerpt: "Our nutrition awareness programme distributed healthy meals to school children across rural Jharkhand villages.",
  },
  {
    image: IMAGES.distribution,
    date: "September 28, 2025",
    title: "Seed Distribution Drive Benefits 500 Farming Families",
    excerpt: "Certified seeds and modern farming tools distributed to 500+ families across 12 villages in Khunti district.",
  },
];

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.heroHome})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B4332]/70 via-[#1B4332]/55 to-[#1B4332]/75" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <ScrollReveal>
            <p className="text-[#D4A843] text-sm uppercase tracking-[0.25em] font-bold font-outfit mb-6">
              Empowering Lives. Building Futures.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-7xl text-white font-bold leading-[1.1] mb-6">
              Transforming Jharkhand,<br />One Life at a Time.
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-white/85 text-base sm:text-lg lg:text-xl font-outfit max-w-3xl mx-auto mb-10 leading-relaxed">
              From tribal villages to thriving communities — we build schools, hospitals,
              farmer networks, and youth movements that create lasting, measurable change
              across rural India.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-involved" data-testid="hero-cta-join" className="bg-[#D4A843] text-[#1B4332] px-10 py-4 rounded-xl font-bold font-outfit text-base hover:bg-[#D4A843]/90 hover:shadow-xl hover:scale-[1.02] transition-all">
                Join the Movement
              </Link>
              <a href="#impact-strip" data-testid="hero-cta-explore" className="border-2 border-white/80 text-white px-10 py-4 rounded-xl font-medium font-outfit text-base hover:bg-white/10 hover:border-white transition-all">
                Explore Our Impact
              </a>
            </div>
          </ScrollReveal>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Impact Ticker */}
      <section id="impact-strip" data-testid="impact-ticker" className="bg-[#1B4332] py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            {stats.map((stat, i) => (
              <div key={i} className="text-center relative">
                <div className="text-[#D4A843] font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold mb-2">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-white/80 text-sm font-outfit tracking-wide">{stat.label}</p>
                {i < 3 && <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-12 bg-white/15" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Arjun Munda Highlight - Government Credibility */}
      <section data-testid="arjun-munda-section" className="bg-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#D4A843]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <ScrollReveal>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#D4A843]/30 rounded-2xl" />
                <img
                  src={IMAGES.arjunMunda}
                  alt="Shri Arjun Munda Ji, Union Minister, with MBT Trust beneficiaries"
                  className="w-full h-80 md:h-[28rem] object-cover rounded-2xl shadow-2xl relative z-10"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 right-4 z-20 bg-[#1B4332]/90 backdrop-blur-sm rounded-xl px-5 py-3">
                  <p className="text-[#D4A843] font-playfair font-bold text-sm">
                    Shri Arjun Munda Ji, Union Minister
                  </p>
                  <p className="text-white/80 text-xs font-outfit">with MBT Trust beneficiaries in Jharkhand</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">
                  Trusted at the Highest Levels
                </p>
                <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold mb-6 leading-tight">
                  Leadership Meets Grassroots Impact
                </h2>
                <p className="text-[#333] text-base font-outfit leading-relaxed mb-6">
                  MBT Welfare & Development Trust's impact on the ground has earned recognition
                  from the highest levels of government. Shri Arjun Munda Ji, Union Minister of
                  Tribal Affairs, has personally engaged with our beneficiaries — a testament to
                  the Trust's credibility, transparency, and measurable impact in transforming
                  tribal communities across Jharkhand.
                </p>
                <p className="text-[#333] text-base font-outfit leading-relaxed mb-6">
                  This is not just an NGO — this is an institutional-grade trust that works with
                  government bodies, elected representatives, and district administration to
                  drive systemic change.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="bg-[#F0F7F4] border border-[#2D6A4F]/15 rounded-lg px-4 py-3 text-center">
                    <p className="text-[#D4A843] font-playfair font-bold text-xl">20+</p>
                    <p className="text-[#666] text-xs font-outfit">Years on Ground</p>
                  </div>
                  <div className="bg-[#F0F7F4] border border-[#2D6A4F]/15 rounded-lg px-4 py-3 text-center">
                    <p className="text-[#D4A843] font-playfair font-bold text-xl">4</p>
                    <p className="text-[#666] text-xs font-outfit">Districts Active</p>
                  </div>
                  <div className="bg-[#F0F7F4] border border-[#2D6A4F]/15 rounded-lg px-4 py-3 text-center">
                    <p className="text-[#D4A843] font-playfair font-bold text-xl">Govt.</p>
                    <p className="text-[#666] text-xs font-outfit">Recognized</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section data-testid="who-we-are" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ScrollReveal>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">About the Trust</p>
                <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold mb-6 leading-tight">
                  Who We Are
                </h2>
                <p className="text-[#333] text-base font-outfit leading-relaxed mb-4">
                  MBT Welfare & Development Trust is a grassroots social impact organisation
                  rooted in the soil of Jharkhand. Founded on the conviction that true
                  development begins at the community level, we operate at the intersection
                  of healthcare, education, agriculture, women's empowerment, and youth leadership.
                </p>
                <p className="text-[#333] text-base font-outfit leading-relaxed mb-4">
                  We are not a charity that parachutes in with temporary relief. We are an
                  embedded, community-led platform that builds permanent infrastructure —
                  physical, social, and economic — so that villages become self-sustaining
                  ecosystems of growth.
                </p>
                <p className="text-[#666] text-sm font-outfit mb-6 italic">
                  Registered Public Charitable Trust | Regd. No: JH/2025/0925229
                </p>
                <Link to="/about" data-testid="who-we-are-link" className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-6 py-3 rounded-xl font-medium font-outfit hover:bg-[#2D6A4F] transition-all">
                  Learn More About Us <ArrowRight size={18} />
                </Link>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                <img src={IMAGES.womenEmpowerment} alt="Women in SHG meeting" className="rounded-xl shadow-lg w-full h-48 sm:h-64 object-cover" loading="lazy" />
                <img src={IMAGES.agriculture} alt="Farmers empowered" className="rounded-xl shadow-lg w-full h-48 sm:h-64 object-cover mt-8" loading="lazy" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section data-testid="programmes-preview" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Our Programmes</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold mb-4">What We Do</h2>
              <p className="text-[#666] font-outfit max-w-2xl mx-auto">
                Six interconnected verticals — each designed to reinforce the others.
                A farmer in our FPO sends her children to our school. A youth in our
                leadership programme volunteers at our health camp.
              </p>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {programmes.map((prog, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div data-testid={`programme-card-${i}`} className="bg-white rounded-xl p-6 md:p-8 border border-[#2D6A4F]/10 shadow-[0_4px_20px_rgba(27,67,50,0.06)] hover:shadow-[0_12px_40px_rgba(27,67,50,0.14)] hover:-translate-y-1.5 transition-all group cursor-pointer">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${prog.color}10` }}>
                    <prog.icon size={24} style={{ color: prog.color }} />
                  </div>
                  <h3 className="font-playfair text-xl text-[#1B4332] font-semibold mb-3">{prog.title}</h3>
                  <p className="text-[#666] text-sm font-outfit leading-relaxed">{prog.desc}</p>
                  <div className="mt-4 h-0.5 w-0 bg-[#D4A843] group-hover:w-16 transition-all duration-300" />
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <div className="text-center mt-12">
              <Link to="/programmes" data-testid="explore-programmes-link" className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-8 py-4 rounded-xl font-medium font-outfit hover:bg-[#2D6A4F] hover:shadow-lg transition-all">
                Explore All Programmes <ArrowRight size={18} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* The MBT Model - How We Work */}
      <section data-testid="mbt-model-home" className="bg-[#1B4332] py-16 md:py-24 lg:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Our Approach</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-white font-bold mb-4">The MBT Model</h2>
              <p className="text-white/70 font-outfit max-w-2xl mx-auto">
                We operate on a distinctive integrated development model that sets us apart
                from conventional NGOs.
              </p>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {modelSteps.map((step, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#D4A843] flex items-center justify-center mx-auto mb-5">
                    <step.icon size={28} className="text-[#1B4332]" />
                  </div>
                  <div className="text-[#D4A843]/60 font-outfit font-bold text-xs tracking-wider mb-2">STEP {i + 1}</div>
                  <h3 className="font-playfair text-lg text-white font-semibold mb-2">{step.title}</h3>
                  <p className="text-white/60 text-sm font-outfit leading-relaxed">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Gallery Strip */}
      <section data-testid="gallery-strip" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">From The Field</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Our Work in Action</h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[IMAGES.agriculture, IMAGES.childrenBanner2, IMAGES.healthcare, IMAGES.seeds, IMAGES.womenEmpowerment, IMAGES.youth, IMAGES.childrenBanner1, IMAGES.events].map((img, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div className="overflow-hidden rounded-xl group aspect-square">
                  <img src={img} alt={`MBT Trust field work ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <div className="text-center mt-10">
              <Link to="/impact" className="inline-flex items-center gap-2 text-[#1B4332] font-semibold font-outfit border-2 border-[#1B4332] px-8 py-3 rounded-xl hover:bg-[#1B4332] hover:text-white transition-all">
                View Full Impact <ArrowRight size={18} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Testimonial */}
      <section data-testid="testimonial-section" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <img src={IMAGES.seeds} alt="Beneficiary" className="w-full md:w-72 h-72 rounded-xl object-cover shadow-lg flex-shrink-0" loading="lazy" />
              <div>
                <Quote size={48} className="text-[#D4A843]/30 mb-4" />
                <blockquote className="font-playfair text-xl sm:text-2xl text-[#1B4332] leading-relaxed italic mb-6">
                  "Before MBT, I had no way to sell my crops at a fair price. Now,
                  through the FPO, I earn twice what I used to — and my daughter is
                  in school for the first time."
                </blockquote>
                <div className="h-0.5 w-12 bg-[#D4A843] mb-4" />
                <p className="text-[#333] font-outfit font-semibold">Farmer, Khunti District</p>
                <p className="text-[#666] font-outfit text-sm">FPO Member since 2022</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section data-testid="mission-vision-home" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="bg-[#1B4332] rounded-2xl p-8 md:p-10 text-white h-full">
                <Star size={32} className="text-[#D4A843] mb-4" />
                <h3 className="font-playfair text-2xl font-semibold mb-4 text-[#D4A843]">Our Mission</h3>
                <p className="font-outfit leading-relaxed text-white/85">
                  To uplift the most underserved communities across Jharkhand through integrated
                  programmes in healthcare, education, agriculture, and empowerment — creating
                  self-sustaining ecosystems where every individual can thrive with dignity.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="bg-white rounded-2xl p-8 md:p-10 border-2 border-[#D4A843] h-full">
                <Star size={32} className="text-[#1B4332] mb-4" />
                <h3 className="font-playfair text-2xl font-semibold mb-4 text-[#1B4332]">Our Vision</h3>
                <p className="font-outfit leading-relaxed text-[#333]">
                  A Jharkhand where no village is forgotten, no farmer goes unsupported, no child
                  is denied education, and no woman is held back — a state that leads India in
                  inclusive, ground-up development by 2040.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section data-testid="cta-banner" className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.childrenBanner1})` }} />
        <div className="absolute inset-0 bg-[#1B4332]/80" />
        <div className="absolute top-0 left-0 w-full h-1 bg-[#D4A843]" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-white font-bold mb-4">
              Be Part of the Movement
            </h2>
            <p className="text-white/80 font-outfit text-base sm:text-lg mb-10 max-w-2xl mx-auto">
              Whether you volunteer, partner, or contribute — every action builds a
              stronger Jharkhand. Join 5,000+ farmers, 1,800+ families, and hundreds
              of youth volunteers who are already part of this transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-involved" data-testid="cta-volunteer" className="bg-[#D4A843] text-[#1B4332] px-10 py-4 rounded-xl font-bold font-outfit hover:bg-[#D4A843]/90 hover:shadow-xl transition-all">
                Get Involved
              </Link>
              <Link to="/contact" data-testid="cta-contact" className="border-2 border-white text-white px-10 py-4 rounded-xl font-medium font-outfit hover:bg-white/10 transition-all">
                Contact Us
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Latest from the Ground */}
      <section data-testid="blog-preview" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">News & Updates</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Latest from the Ground</h2>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {blogPosts.map((post, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <Link to="/stories" data-testid={`blog-card-${i}`} className="block bg-white rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(27,67,50,0.06)] hover:shadow-[0_12px_40px_rgba(27,67,50,0.14)] hover:-translate-y-1.5 transition-all">
                  <img src={post.image} alt={post.title} className="w-full h-52 object-cover" loading="lazy" />
                  <div className="p-6">
                    <p className="text-[#D4A843] text-xs font-outfit font-semibold mb-2">{post.date}</p>
                    <h3 className="font-playfair text-lg text-[#1B4332] font-semibold mb-2 leading-snug">{post.title}</h3>
                    <p className="text-[#666] text-sm font-outfit leading-relaxed">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-[#D4A843] text-sm font-semibold font-outfit mt-4">
                      Read More <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <div className="text-center mt-12">
              <Link to="/stories" data-testid="view-all-stories" className="inline-flex items-center gap-2 text-[#1B4332] font-semibold font-outfit border-2 border-[#1B4332] px-8 py-3 rounded-xl hover:bg-[#1B4332] hover:text-white transition-all">
                View All Stories <ArrowRight size={18} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
